import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PeriodOption } from './OpportunityHero.type';
import {
  LOEStatus,
  OpportunityTile,
  SparklineData,
} from 'Interfaces/ProductWikiInterfaces/opportunityAtAGlance.types';
import { ProductAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { DateConversionUtility } from 'utils/dateUtilities';

export const useOpportunityHero = (
  productAnalysisStore: ProductAnalysisStore,
  tiles: OpportunityTile[],
  loeStatus: LOEStatus,
  sparklineData: SparklineData,
  onDataUpdate: (
    tiles: OpportunityTile[],
    loeStatus: LOEStatus,
    sparklineData: SparklineData
  ) => void
) => {
  const [searchParams] = useSearchParams();
  const gfc = searchParams.get('gfc');

  // Initialize period from store if available, otherwise default to '3 months'
  const [selectedPeriod, setSelectedPeriod] = useState<string>('3 months');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [showCustomDateModal, setShowCustomDateModal] = useState<boolean>(false);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
  const [tempToDate, setTempToDate] = useState<Date | null>(null);
  const [previousPeriod, setPreviousPeriod] = useState<string>('3 months');

  const periodOptions: PeriodOption[] = [
    { label: '3 months', value: '3 months' },
    { label: '6 months', value: '6 months' },
    { label: '12 months', value: '12 months' },
    { label: 'Custom', value: 'custom' },
  ];

  // Track previous store data to prevent unnecessary updates
  const prevStoreDataRef = useRef<{
    tiles?: OpportunityTile[];
    loeStatus?: LOEStatus;
    sparklineData?: SparklineData;
  } | null>(null);

  // Effect to update parent component when store data changes
  useEffect(() => {
    if (
      productAnalysisStore.opportunityOverview &&
      productAnalysisStore.sparklineData &&
      onDataUpdate
    ) {
      const currentTiles = productAnalysisStore.opportunityOverview.tiles;
      const currentLoeStatus = productAnalysisStore.opportunityOverview.loeStatus;
      const currentSparklineData = productAnalysisStore.sparklineData;

      // Only update if data actually changed
      const tilesChanged =
        JSON.stringify(currentTiles) !== JSON.stringify(prevStoreDataRef.current?.tiles);
      const loeChanged =
        JSON.stringify(currentLoeStatus) !== JSON.stringify(prevStoreDataRef.current?.loeStatus);
      const sparklineChanged =
        JSON.stringify(currentSparklineData) !==
        JSON.stringify(prevStoreDataRef.current?.sparklineData);

      if (tilesChanged || loeChanged || sparklineChanged) {
        prevStoreDataRef.current = {
          tiles: currentTiles,
          loeStatus: currentLoeStatus,
          sparklineData: currentSparklineData,
        };
        onDataUpdate(currentTiles, currentLoeStatus, currentSparklineData);
      }
    }
  }, [productAnalysisStore.opportunityOverview, productAnalysisStore.sparklineData, onDataUpdate]);

  const handlePeriodChange = async (e: { value: string }) => {
    if (e.value === 'custom') {
      setPreviousPeriod(selectedPeriod);
      setShowCustomDateModal(true);
      setTempFromDate(fromDate);
      setTempToDate(toDate);
      setSelectedPeriod('custom');
    } else {
      setSelectedPeriod(e.value);
      setFromDate(null);
      setToDate(null);
      setShowCustomDateModal(false);

      // Call API when period changes (if GFC is available)
      if (gfc) {
        await productAnalysisStore.updatePeriodAndFetch(
          e.value as '3 months' | '6 months' | '12 months'
        );
      }
    }
  };

  const handleApply = async () => {
    if (!tempFromDate || !tempToDate || !gfc) {
      return;
    }

    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setSelectedPeriod('custom');
    setShowCustomDateModal(false);

    // Call API with custom date range
    const fromDateStr = DateConversionUtility.convertLocalToDBDateString(tempFromDate);
    const toDateStr = DateConversionUtility.convertLocalToDBDateString(tempToDate);

    await productAnalysisStore.updatePeriodAndFetch('custom', fromDateStr, toDateStr);
  };

  const handleCancel = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setShowCustomDateModal(false);
    setSelectedPeriod(previousPeriod);
  };

  const handleDropdownFocus = () => {
    // If custom is already selected and modal is not open, open it with current dates
    if (selectedPeriod === 'custom' && !showCustomDateModal) {
      setShowCustomDateModal(true);
      setTempFromDate(fromDate);
      setTempToDate(toDate);
    }
  };

  // Sync selectedPeriod with store's currentPeriod
  useEffect(() => {
    if (productAnalysisStore.currentPeriod) {
      setSelectedPeriod(productAnalysisStore.currentPeriod);
    }
  }, [productAnalysisStore.currentPeriod]);

  // Sync dates with store
  useEffect(() => {
    if (productAnalysisStore.currentFromDate) {
      const parsedDate = DateConversionUtility.convertDBDateToLocal(
        productAnalysisStore.currentFromDate
      );
      if (parsedDate) {
        setFromDate(parsedDate);
      }
    }
    if (productAnalysisStore.currentToDate) {
      const parsedDate = DateConversionUtility.convertDBDateToLocal(
        productAnalysisStore.currentToDate
      );
      if (parsedDate) {
        setToDate(parsedDate);
      }
    }
  }, [productAnalysisStore.currentFromDate, productAnalysisStore.currentToDate]);

  const getHeadingText = (): string => {
    const periodToUse = selectedPeriod || productAnalysisStore.currentPeriod || '3 months';
    if (periodToUse === 'custom' && fromDate && toDate) {
      return `Opportunity at-a-Glance from ${DateConversionUtility.convertLocalToDBDateString(fromDate)} to ${DateConversionUtility.convertLocalToDBDateString(toDate)}`;
    } else if (
      periodToUse === '3 months' ||
      periodToUse === '6 months' ||
      periodToUse === '12 months'
    ) {
      return `Opportunity at-a-Glance for last ${periodToUse}`;
    }
    return 'Opportunity at-a-Glance';
  };

  // Get current data from store if available, otherwise use props
  const currentTiles = productAnalysisStore.opportunityTiles.length > 0 
    ? productAnalysisStore.opportunityTiles 
    : (productAnalysisStore.opportunityOverview?.tiles || tiles);
  const currentLoeStatus = productAnalysisStore.loeStatus 
    || productAnalysisStore.opportunityOverview?.loeStatus 
    || loeStatus;
  const currentSparklineData = productAnalysisStore.sparklineData || sparklineData;
  // Combined loading state - true if any section is loading
  const isLoading = productAnalysisStore.isTilesLoading 
    || productAnalysisStore.isSparklinesLoading 
    || productAnalysisStore.isMedicalSupplyLoading 
    || productAnalysisStore.isOpportunityLoading;

  // Calculate the number of months for x-axis based on selected period
  const getPeriodMonths = (): number => {
    const periodToUse = selectedPeriod || productAnalysisStore.currentPeriod || '3 months';

    if (periodToUse === '3 months') return 3;
    if (periodToUse === '6 months') return 6;
    if (periodToUse === '12 months') return 12;

    // For custom period, calculate months between dates
    if (periodToUse === 'custom' && fromDate && toDate) {
      const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.ceil(diffDays / 30); // Approximate months
      return Math.max(diffMonths, 1); // At least 1 month
    }

    return 3; // Default to 3 months
  };

  const periodMonths = getPeriodMonths();

  return {
    selectedPeriod,
    fromDate,
    toDate,
    showCustomDateModal,
    tempFromDate,
    tempToDate,
    previousPeriod,
    periodMonths,
    handlePeriodChange,
    handleApply,
    handleCancel,
    handleDropdownFocus,
    getHeadingText,
    currentTiles,
    currentLoeStatus,
    currentSparklineData,
    isLoading,
    periodOptions,
    setSelectedPeriod,
    setFromDate,
    setToDate,
    setShowCustomDateModal,
    setTempFromDate,
    setTempToDate,
    setPreviousPeriod,
  };
};
